'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/styleUtils';
import { VehicleSprite } from '@/modules/captcha/components/CarSprite';
import { PuzzlePrompt } from '@/modules/captcha/components/PuzzleFrame';
import { VEHICLE_COLORS, randomInt, shuffle } from '@/modules/captcha/shared/puzzle-utils';
import { useTapChallenge } from '@/modules/captcha/hooks/useTapChallenge';
import { shakeAnimation, tileBase, tileCorrect, tileIdle, tileWrong } from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

/**
 * Design 2k — tap the car of the named colour.
 *
 * ## Invariants:
 * - Colour names come from `VEHICLE_COLORS` and are translated, not interpolated raw. Why: the
 *   prompt reads "Tap the green car", so the word has to be a translatable term — shipping the
 *   English key into the sentence would leave the one meaningful word untranslated in every
 *   other locale.
 * - Every car in the row is a different colour. Why: the prompt names a colour, so two cars
 *   sharing one would make the challenge ambiguous and score a correct tap as wrong.
 */
export const CarWash = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');
  const [colors] = useState(() => shuffle(VEHICLE_COLORS));
  const [answer] = useState(() => randomInt(VEHICLE_COLORS.length));
  const { wrongIndex, solved, pick } = useTapChallenge(answer, onSolved);

  return (
    <>
      <PuzzlePrompt>
        {t('tapColoredCarPrompt', { color: t(`color_${colors[answer].name}`) })}
      </PuzzlePrompt>
      <div className="tw:mx-auto tw:flex tw:w-[312px] tw:gap-nano" data-cy="captcha-car-wash">
        {colors.map((color, index) => {
          const isWrong = index === wrongIndex;
          const isCorrect = solved && index === answer;
          return (
            <button
              key={color.name}
              type="button"
              onClick={() => pick(index)}
              className={cn(
                tileBase,
                'tw:h-[48px] tw:flex-1',
                !isWrong && !isCorrect && tileIdle,
                isCorrect && tileCorrect,
                isWrong && cn(tileWrong, shakeAnimation)
              )}
              aria-label={t(`color_${color.name}`)}
              data-cy={`captcha-wash-car-${index}`}
            >
              <VehicleSprite kind="car" color={color.hex} />
            </button>
          );
        })}
      </div>
    </>
  );
};

CarWash.displayName = 'CarWash';
